import React from 'react';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import ScoreboardOutlinedIcon from '@mui/icons-material/ScoreboardOutlined';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { EventPushMessage } from '@moment-golf/types';
import { EVENT_PUSH_ICON_LABELS } from '@moment-golf/types';

export type IconKey = EventPushMessage['icon'];

const COPPER = 'rgb(184, 115, 51)';

export const ICON_OPTIONS: { key: IconKey; label: string; element: React.ReactElement }[] = [
    { key: 'stopwatch', label: 'Pace', element: <TimerOutlinedIcon /> },
    { key: 'lightning', label: 'Lightning', element: <BoltOutlinedIcon /> },
    { key: 'score', label: 'Score', element: <ScoreboardOutlinedIcon /> },
    { key: 'sunset', label: 'Daylight', element: <WbSunnyOutlinedIcon /> },
    { key: 'info', label: 'Info', element: <InfoOutlinedIcon /> },
];

export const ICON_MAP: Record<IconKey, React.ReactElement> = {
    stopwatch: <TimerOutlinedIcon sx={{ fontSize: 16, color: COPPER }} />,
    lightning: <BoltOutlinedIcon sx={{ fontSize: 16, color: COPPER }} />,
    score: <ScoreboardOutlinedIcon sx={{ fontSize: 16, color: COPPER }} />,
    sunset: <WbSunnyOutlinedIcon sx={{ fontSize: 16, color: COPPER }} />,
    info: <InfoOutlinedIcon sx={{ fontSize: 16, color: COPPER }} />,
};

export const ICON_MAP_LARGE: Record<IconKey, React.ReactElement> = {
    stopwatch: <TimerOutlinedIcon sx={{ fontSize: 20, color: COPPER }} />,
    lightning: <BoltOutlinedIcon sx={{ fontSize: 20, color: COPPER }} />,
    score: <ScoreboardOutlinedIcon sx={{ fontSize: 20, color: COPPER }} />,
    sunset: <WbSunnyOutlinedIcon sx={{ fontSize: 20, color: COPPER }} />,
    info: <InfoOutlinedIcon sx={{ fontSize: 20, color: COPPER }} />,
};

export function targetLabel(msg: EventPushMessage): string {
    const t = msg.target;
    if (t === 'all' || t == null) return 'All Players';

    if (typeof t === 'object' && t.type === 'teeTime') {
        const slots = Array.isArray(t.slots)
            ? t.slots
            : (t as { teeTime?: string; round?: number }).teeTime != null
                ? [{ teeTime: (t as { teeTime: string }).teeTime, round: (t as { round?: number }).round ?? 0 }]
                : [];
        const count = slots.length;
        return count === 0 ? 'Tee Time' : `${count} Tee Time${count !== 1 ? 's' : ''}`;
    }

    if (typeof t === 'object' && t.type === 'players') {
        const count = t.scoreCodes?.length ?? 0;
        return count === 0 ? 'Player(s)' : `${count} Player${count !== 1 ? 's' : ''}`;
    }

    return 'Unknown target';
}

export function formatPushTimestamp(pushedAt: string): string {
    return new Date(pushedAt).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
    });
}

export function historyEntryLabel(msg: EventPushMessage): string {
    if (msg.message === '') {
        const iconLabel = EVENT_PUSH_ICON_LABELS[msg.icon] ?? msg.icon;
        return msg.clearScope === 'icon' ? `[Cleared — ${iconLabel}]` : '[Cleared — All]';
    }
    return msg.message;
}

export function appendMessageToViewEvent(
    viewEvent: any,
    setViewEvent: (event: any) => void,
    newMsg: EventPushMessage,
): void {
    if (!viewEvent) return;
    const existing: EventPushMessage[] = viewEvent.eventPushMessages ?? [];
    setViewEvent({ ...viewEvent, eventPushMessages: [...existing, newMsg] });
}
